import React, { cloneElement } from 'react';
import Link from 'next/link';
import { PATH_DEMO, PATH_PRICING } from '@/constants/spa-routes';
import { Drawer } from 'vaul';
import { GreenButton } from '@/components/ButtonsCollection/GreenButton/GreenButton';
import { clsx } from 'clsx';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/mobileAccordion';
import Image from 'next/image';
import { Typography } from '@/components/Typography/Typography';
import { NavigationItemsType } from '@/constants/navigation';
import style from '@/components/Header/Header.module.css';

interface MobileMenuProps {
  pathname: string;
  isIncludesRoutes: boolean;
  navigationItems: NavigationItemsType;
}

export const MobileMenu = ({ pathname, isIncludesRoutes, navigationItems }: MobileMenuProps) => {
  const handleChangePageRefreshPosition = () => {
    document.body.style.removeProperty('position');
  };

  return (
    <div>
      <ul className="flex flex-col gap-12">
        {navigationItems.map((item) => {
          const dropdownLinksActive = item.dropdownItems
            ?.map((el) => el.items)
            .flat()
            ?.some((el) =>
              isIncludesRoutes ? pathname.includes(el?.href || '') : pathname === el?.href
            );
          return item?.dropdownItems ? (
            <li key={item.id} className="text-center w-full">
              <Accordion
                type="single"
                collapsible
                defaultValue={dropdownLinksActive ? item.title : ''}
                className="w-full"
              >
                <AccordionItem value={item.title} className="border-b-0">
                  <AccordionTrigger
                    className={clsx(
                      dropdownLinksActive && '[&>svg>path]:stroke-[3px]',
                      'p-0 w-full [&>svg>path]:stroke-black'
                    )}
                  >
                    <p
                      className={clsx(
                        dropdownLinksActive ? 'font-bold' : 'font-medium',
                        'text-[22px] font-jamjuree leading-[100%] text-left flex items-center gap-2 min-w-[115px]'
                      )}
                    >
                      {item.title}{' '}
                      {item?.isNew && <span className="text-green-default text-sm">new</span>}
                    </p>
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col">
                    {item?.dropdownItems.map((dropItem, index) => {
                      return (
                        <div key={dropItem.id} className={clsx('flex flex-col z-50 pt-6')}>
                          <div className="flex items-center gap-2.5">
                            <Typography
                              $weight="600"
                              $fontSizeSm="13px"
                              $fontSizeXs="13px"
                              className="font-jamjuree opacity-30 uppercase"
                            >
                              {dropItem.title}
                            </Typography>
                            {dropItem?.isCategorySoon && (
                              <Typography
                                $type="PMini"
                                $fontSizeSm="10px"
                                $fontSizeXs="10px"
                                $weight="600"
                                className="uppercase bg-[#C4D1CE] rounded-full px-2 py-1 inline opacity-50"
                              >
                                soon
                              </Typography>
                            )}
                          </div>
                          <ul>
                            {dropItem?.items?.map((elements) => {
                              const listLinksActive = isIncludesRoutes
                                ? pathname.includes(elements.href)
                                : pathname === elements.href;
                              const listLinksAdditionalActive = isIncludesRoutes
                                ? pathname.includes(elements?.additionalButton?.href || '')
                                : pathname === elements.additionalButton?.href;
                              return (
                                <li key={elements.id}>
                                  <div className="flex flex-col gap-4 py-6 border-b border-[#05050D0D]">
                                    <Drawer.Close
                                      key={elements.id}
                                      disabled={elements.comingSoon}
                                      className={clsx(
                                        elements.comingSoon && 'pointer-events-none opacity-60'
                                      )}
                                      onClick={handleChangePageRefreshPosition}
                                    >
                                      <Link
                                        prefetch={false}
                                        href={elements.href}
                                        className="!no-underline flex items-start gap-3.5"
                                      >
                                        <div className="h-[35px] w-[35px] flex items-center">
                                          {typeof elements.icon === 'string' ? (
                                            <Image
                                              width="0"
                                              height="0"
                                              sizes="100vw"
                                              className={'w-full h-full object-contain'}
                                              alt={`${elements.title} image`}
                                              src={elements.icon}
                                            />
                                          ) : (
                                            elements.icon &&
                                            cloneElement(elements.icon, {
                                              height: '35px',
                                              width: '35px',
                                            })
                                          )}
                                        </div>
                                        <div className="flex flex-col gap-2">
                                          <p
                                            className={clsx(
                                              listLinksActive ? 'font-bold' : 'font-semibold',
                                              'text-[16px] font-jamjuree leading-[100%] text-left'
                                            )}
                                          >
                                            {elements.title}
                                          </p>
                                          <div className="flex">
                                            <p
                                              className={clsx(
                                                'text-[16px] opacity-70 font-jamjuree leading-[145%] font-normal text-left'
                                              )}
                                            >
                                              {elements.description}
                                            </p>
                                            {elements.comingSoon && (
                                              <div>
                                                <Typography
                                                  $type="PMini"
                                                  $fontSizeSm="10px"
                                                  $fontSizeXs="10px"
                                                  $weight="600"
                                                  className="uppercase bg-[#C4D1CE] rounded-full px-2 py-1 inline"
                                                >
                                                  soon
                                                </Typography>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </Link>
                                    </Drawer.Close>
                                    {elements.additionalButton?.href && (
                                      <Drawer.Close
                                        disabled={elements.additionalButton.comingSoon}
                                        className={clsx(
                                          elements.additionalButton.comingSoon &&
                                            'pointer-events-none opacity-60'
                                        )}
                                        onClick={handleChangePageRefreshPosition}
                                      >
                                        <Link
                                          prefetch={false}
                                          href={elements.additionalButton.href}
                                          className="!no-underline flex items-center gap-3.5"
                                        >
                                          <div className="h-[35px] w-[35px] flex items-center">
                                            {typeof elements.additionalButton.icon === 'string' ? (
                                              <Image
                                                width="0"
                                                height="0"
                                                sizes="100vw"
                                                className={'w-full h-full object-contain'}
                                                alt={`${elements.additionalButton.title} image`}
                                                src={elements.additionalButton.icon}
                                              />
                                            ) : (
                                              elements.additionalButton.icon &&
                                              cloneElement(elements.additionalButton.icon, {
                                                height: '35px',
                                                width: '35px',
                                              })
                                            )}
                                          </div>
                                          <p
                                            className={clsx(
                                              listLinksAdditionalActive
                                                ? 'font-bold'
                                                : 'font-semibold',
                                              'text-[16px] font-jamjuree leading-[100%] text-left'
                                            )}
                                          >
                                            {elements.additionalButton.title}
                                          </p>
                                          {elements.additionalButton.comingSoon && (
                                            <div>
                                              <Typography
                                                $type="PMini"
                                                $fontSizeSm="10px"
                                                $fontSizeXs="10px"
                                                $weight="600"
                                                className="uppercase bg-[#C4D1CE] rounded-full px-2 py-1 inline"
                                              >
                                                soon
                                              </Typography>
                                            </div>
                                          )}
                                        </Link>
                                      </Drawer.Close>
                                    )}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      );
                    })}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </li>
          ) : (
            <li>
              <Drawer.Close
                key={item.id}
                className="w-full"
                onClick={handleChangePageRefreshPosition}
              >
                <Link href={item?.href || ''} className="!no-underline" prefetch={false}>
                  <p
                    className={clsx(
                      isIncludesRoutes
                        ? pathname.includes(item?.href || '')
                        : pathname === item.href
                          ? 'font-bold'
                          : 'font-medium',
                      'text-[22px] font-jamjuree leading-[100%] text-left'
                    )}
                  >
                    {item.title}
                  </p>
                </Link>
              </Drawer.Close>
            </li>
          );
        })}
        <li>
          <Drawer.Close className="w-full" onClick={handleChangePageRefreshPosition}>
            <Link href={PATH_PRICING.ROOT} className="!no-underline" prefetch={false}>
              <p
                className={clsx(
                  isIncludesRoutes
                    ? pathname.includes(PATH_PRICING.ROOT)
                    : pathname === PATH_PRICING.ROOT
                      ? 'font-bold'
                      : 'font-medium',
                  'text-[22px] font-jamjuree leading-[100%] text-left'
                )}
              >
                Pricing
              </p>
            </Link>
          </Drawer.Close>
        </li>
        <div className="flex flex-col gap-6">
          <li>
            <Link
              href={PATH_DEMO.ROOT}
              prefetch={false}
              className="!no-underline w-full"
              onClick={handleChangePageRefreshPosition}
            >
              <button
                className={`green-button w-full [&_span]:hover:text-black ${style.headerDemoButton}`}
              >
                <div className="button-text">
                  <span className="text-white">See in Action</span>
                </div>
                <div className="gradient"></div>
              </button>
            </Link>
          </li>
          <li
            className="flex justify-center items-center"
            onClick={handleChangePageRefreshPosition}
          >
            <GreenButton
              url={PATH_DEMO.ROOT}
              linkBtnClassName="w-full !min-w-fit"
              className="w-full"
            >
              <span>Get Demo Access</span>
            </GreenButton>
          </li>
        </div>
      </ul>
    </div>
  );
};
